import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ArrowRight, Play, Zap, Shield, Globe } from "lucide-react";
import { Button } from "../ui/Button";
import { Link } from "react-router-dom";

const HeroSectionWrapper = styled.section`
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const BackgroundContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;
  
  &.blob-1 {
    top: 0;
    left: 25%;
    width: 600px;
    height: 600px;
    background: ${({ theme }) => theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20;
  }
  
  &.blob-2 {
    bottom: 0;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20;
    animation-delay: 2s;
  }
  
  &.blob-3 {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 800px;
    height: 800px;
    background: ${({ theme }) => theme.colors.light?.primary || "hsl(258, 90%, 58%)"}05;
  }
`;

const FloatingBadge = styled.div`
  position: absolute;
  display: none;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.glass?.light?.background || "hsla(0, 0%, 100%, 0.8)"};
  backdrop-filter: blur(20px);
  box-shadow: ${({ theme }) => theme.shadows.soft};
  animation: float 6s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay || 0}s;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: flex;
  }
  
  &.top-left {
    top: 20%;
    left: 10%;
  }
  
  &.top-right {
    top: 40%;
    right: 10%;
  }
  
  &.bottom-left {
    bottom: 32%;
    left: 20%;
  }
`;

const FloatingIcon = styled.div`
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
`;

const FloatingText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;
  position: relative;
  z-index: 10;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 0 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 0 32px;
  }
`;

const Content = styled.div`
  max-width: 896px;
  margin: 0 auto;
  text-align: center;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.glass?.light?.background || "hsla(0, 0%, 100%, 0.8)"};
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  margin-bottom: 32px;
  animation: fade-up 0.6s ease-out forwards;
`;

const PulsingDot = styled.span`
  position: relative;
  display: flex;
  width: 8px;
  height: 8px;
  
  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.light?.primary || "hsl(258, 90%, 58%)"};
    animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  
  &::after {
    content: "";
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.light?.primary || "hsl(258, 90%, 58%)"};
  }
  
  @keyframes ping {
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const BadgeText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
`;

const Headline = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.hero};
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
  animation: fade-up 0.6s ease-out 0.1s forwards;
  opacity: 0;
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subheadline = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 40px 0;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  animation: fade-up 0.6s ease-out 0.2s forwards;
  opacity: 0;
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 64px;
  animation: fade-up 0.6s ease-out 0.3s forwards;
  opacity: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 576px;
  margin: 0 auto;
  animation: scale-in 0.5s ease-out 0.5s forwards;
  opacity: 0;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.5rem, 3vw, 1.875rem);
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
`;

const BottomFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 128px;
  background: linear-gradient(to top, ${({ theme }) => theme.colors.light?.background || "hsl(0, 0%, 99%)"}, transparent);
  
  body.dark & {
    background: linear-gradient(to top, ${({ theme }) => theme.colors.dark?.background || "hsl(240, 20%, 6%)"}, transparent);
  }
`;

const statKeys = [
  { labelKey: "landing.hero.activeUsers", value: "50K+" },
  { labelKey: "landing.hero.companies", value: "2,500+" },
  { labelKey: "landing.hero.countries", value: "120+" },
];

const floatingFeaturesConfig = [
  { icon: Zap, labelKey: "landing.hero.lightningFast", position: "top-left" },
  { icon: Shield, labelKey: "landing.hero.enterpriseSecurity", position: "top-right" },
  { icon: Globe, labelKey: "landing.hero.globalScale", position: "bottom-left" },
];

export function HeroSection() {
  const { t } = useTranslation();
  return (
    <HeroSectionWrapper>
      <BackgroundContainer>
        <Blob className="blob-1" />
        <Blob className="blob-2" />
        <Blob className="blob-3" />
      </BackgroundContainer>

      {floatingFeaturesConfig.map((feature, index) => {
        const Icon = feature.icon;
        return (
          <FloatingBadge key={feature.labelKey} className={feature.position} $delay={index * 0.5}>
            <FloatingIcon>
              <Icon size={16} />
            </FloatingIcon>
            <FloatingText>{t(feature.labelKey)}</FloatingText>
          </FloatingBadge>
        );
      })}

      <Container>
        <Content>
          <Badge>
            <PulsingDot />
            <BadgeText>{t("landing.hero.badge")}</BadgeText>
          </Badge>

          <Headline>
            {t("landing.hero.headline")} <GradientText>{t("landing.hero.headlineHighlight")}</GradientText> {t("landing.hero.headlineSuffix")}
          </Headline>

          <Subheadline>{t("landing.hero.subheadline")}</Subheadline>

          <CTAWrapper>
            <Link to="/signup" style={{ textDecoration: "none" }}>
              <Button variant="hero" size="xl">
                {t("landing.hero.startFreeTrial")}
                <ArrowRight size={20} />
              </Button>
            </Link>
            <Link to="/features" style={{ textDecoration: "none" }}>
              <Button variant="hero-outline" size="xl">
                <Play size={20} />
                {t("landing.hero.watchDemo")}
              </Button>
            </Link>
          </CTAWrapper>

          <StatsGrid>
            {statKeys.map((stat) => (
              <StatItem key={stat.labelKey}>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{t(stat.labelKey)}</StatLabel>
              </StatItem>
            ))}
          </StatsGrid>
        </Content>
      </Container>

      <BottomFade />
    </HeroSectionWrapper>
  );
}
